import React, { useEffect, useState, useRef } from 'react';
import { Menu } from 'models/BaseModel'
import 'assets/styles/Theme10.css';
import 'assets/styles/Reset.css';
import 'assets/styles/App.css';
import 'assets/styles/Panel.css';
import { Files } from 'helpers/FileHelpers';
import { Extract } from 'helpers/ExtractHelper';
import { Code, Title } from 'constants/codes';
import { Actions } from 'helpers/CommonHelper';
import { Client } from 'models/ClientModel';
import { Control } from 'components/common/ControlManager'
import { Utility } from 'helpers/UtilityHelpers';
import { Form, useNavigate } from "react-router-dom";
import { Hooks } from 'hooks/commonHooks';
import { Name } from 'constants/fieldname';
import { Button, Field, Info } from 'helpers/CalendarImports';
import { API } from 'helpers/ApiHelpers';
import { Message } from 'constants/messages';

type Tab = { visible: boolean; title: string; link: string };

declare global {
  interface Window {
    openForm?: (title: string, link: string) => () => void;
  }
}

const Panel: React.FC = () => {
  const [isSettingVisible, setIsSettingVisible] = useState(false);
  const [isMenuStripVisible, setIsMenuStripVisible] = useState(true);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [menuData, setMenuData] = useState<Menu[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>();
  const emptyTabs = Array(20).fill({ visible: false, title: '', link: '' });
  const [visibleTabs, setVisibleTabs] = useState<Tab[]>(emptyTabs);
  const [profileUrl, setProfileUrl] = useState<string | null >(null);
  const [viewLocation, setViewLocation] = useState<boolean>(false);
  const [locationOptions, setLocationOptions] = Hooks.Form.Options();
  const [selectedlocations, setSelectedLocation] = Hooks.Form.Options();
  const [locationId, setLocationId] = useState<number>(1);
  const profileRef = useRef<HTMLDivElement>(null);
  let typingTimer: NodeJS.Timeout;

  const [searchKey, setSearchKey] = useState('');
  const [results, setResults] = useState<Client[]>([]);
  const navigate = useNavigate();

  const companyName = Utility.Storage.CompanyName();
  const locationName = Utility.Storage.LocationName();
  const locationColor = Utility.Storage.LocationColor() || "#0073aa";
  const userName = Utility.Storage.UserName();
  const userId = Utility.Storage.UserId();

  const PriTasks= Hooks.Privilege.List(Code.Form.Id.TASKS);
  const PriNote= Hooks.Privilege.List(Code.Form.Id.NOTE);

  useEffect(() => {

    Files.Json.API(Code.File.LOCATION, Code.Option.Common.NOTHING, setLocationOptions, setError);
    const locationId = Utility.Storage.LocationId();
    setLocationId(locationId);
  }, []);

  useEffect(() => {
    if(locationOptions){
      const locationList = Utility.Convert.HashString(Utility.Storage.LocationList());
      if(locationList && locationList.length>0){
        const locList = locationList.split(",").map(Number);
        const selectedLoc = locationOptions.filter(x => locList.includes(x.value));
        if(selectedLoc){
          setSelectedLocation(selectedLoc);
        }
      } 
      else setSelectedLocation(locationOptions); 
    }
  }, [locationOptions]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsSettingVisible(false);
      }
    };

    if (isSettingVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSettingVisible]);
    
  const openForm = (title: string, link: string, isActive: boolean = true) => {
    return () => {
      //console.log("openForm:",title,link);
      setVisibleTabs((prevState) => {
        const updatedTabs = [...prevState];
        title = title.replace("  "," ");
        // Check if the tab with the given title already exists
        const existingTabIndex = updatedTabs.findIndex((tab) => tab.title === title);

        if (existingTabIndex !== -1) {
          // If tab exists, make it active
          setActiveTab(`panel${existingTabIndex + 1}`);
        } else {
          // Find the first hidden tab and make it visible and set link
          for (let i = 0; i < updatedTabs.length; i++) {
            if (!updatedTabs[i].visible) {
              updatedTabs[i] = {
                visible: true,
                title: title,
                link: `${Code.Path.LOCAL}${link}`,
              };
              // Make the new tab active immediately
              if(isActive) setActiveTab(`panel${i + 1}`); 

              // Return updated tabs state immediately
              return updatedTabs; 
            }
          }
          alert('All tabs are opened');
        }
        // Return updated tabs state if no changes were made
        return updatedTabs; 
      });
    };
  };

  window.openForm = openForm;

  const reloadActiveTab = () => {
    const reloadIcon = document.querySelector(".reload");
  
    if (reloadIcon) {
      reloadIcon.classList.add("rotate");
  
      // Remove the rotation class after the animation completes (1s)
      setTimeout(() => {
        reloadIcon.classList.remove("rotate");
      }, 1000); // Remove after 1 second
    }
  
    // Find the active iframe with class "frame active"
    const iframe = document.querySelector(".frame.active") as HTMLIFrameElement;
  
    if (iframe) {
      iframe.src = iframe.src; // Reload the iframe
      //console.log("Reloaded:", iframe.src);
    } else {
      //console.log("No active iframe found.");
    }
  };

  const handleTabClose = (index: number) => {

    //console.log("handleTabClose:",index);

    setVisibleTabs((prevState) => {
      const updatedTabs = [...prevState];
      updatedTabs[index] = { visible: false, title: '', link: '' }; // Hide the tab and clear link
      return updatedTabs;
    });
  
    // If the closed tab was the active tab, find the next available tab
    if (activeTab === `panel${index + 1}`) {
      const nextActiveIndex = visibleTabs.findIndex((tab, i) => tab.visible && i !== index);
  
      if (nextActiveIndex !== -1) {
        setActiveTab(`panel${nextActiveIndex + 1}`);
      } else {
        setActiveTab(''); // No tabs left
      }
    }
  };

  (window as any).handleTabClose = handleTabClose;

  const clearAllTabs = () => {
    setVisibleTabs(emptyTabs); // Set the tabs to an empty array
  };

  const handleMiddleClick = (event: React.MouseEvent, index: number) => {
    if (event.button === 1) { // Middle mouse button (wheel click)
      event.preventDefault();
      handleTabClose(index);
    }
  };

  const toggleSetting = () => {
    setIsSettingVisible(!isSettingVisible);
  };

  const toggleMenuStrip = () => {
    setIsMenuStripVisible(!isMenuStripVisible);
  };

  const toggleSubMenu = (menuId: number) => {
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  const activateTab = (tabId: string) => {
    setActiveTab(tabId);
  };

  const getClientList = async (searchKey: string) => {
    if (searchKey.length<=2) {
      setResults([]);
      return;
    } 
    try {
      const companyId = Utility.Storage.CompanyId();
      const databaseId = Utility.Storage.DatabaseId();
      if(searchKey.includes('#')) searchKey = searchKey.replaceAll('#','.');
      const url = `${Code.Path.SERVER}custom/client/${databaseId}/${companyId}/${searchKey}`;
      const result = await API.Data.GetAll(url);
      if (result.code && result.code !== Message.Code.SUCCESS) return []; 
      const data: Client[] = result.data;

      if (Array.isArray(data)) {
        setResults(data);
      } else {
        setResults([]); 
      }

    } catch (error) {
      console.error('Error fetching search results:', error);
      setResults([]);  
    } 
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchKey(value);

    // Clear previous timer
    clearTimeout(typingTimer);

    // Set new timer for 500ms
    typingTimer = setTimeout(() => {
      getClientList(value);
    }, 500);
  };
 
  // const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   //console.log("Search Key:",event.target.value);
  //   const value = event.target.value;
  //   setSearchKey(value);
  //   getClientList(value);
  // };

  const handleClientClick = (event: React.MouseEvent<HTMLLIElement>, title: string, link: number) => {
    openForm(title, `client/${link}`)();

    if (!event.ctrlKey) {
      clearSearch(); // Hide the list only if the Control key is pressed
    }
  };

  const clearSearch = () => {
    setResults([]);
    setSearchKey('');
  }

  const logoutClicked = () => {
    // Delete single localStorage
    // Utility.Storage.Delete(Code.LocalKey.RefreshToken);

    // Clear all localStorage except specified keys
    // const keepLocalStorage = ['key1', 'key2', 'key3'];
    // const allKeys = Object.keys(localStorage);
    // allKeys.forEach(key => {
    //   if (!keepLocalStorage.includes(key)) {
    //     localStorage.removeItem(key);
    //   }
    // });

    // Deleted specified localStorage keys
    // const selectedLocalStorage = ['key1', 'key2', 'key3'];
    // selectedLocalStorage.forEach(key => {
    //   localStorage.removeItem(key);
    // });

    //Clear all localStorage
    localStorage.clear();

    navigate("/");
  }

  const viewChangeLocation = () => {
    setViewLocation(true);
  }

  const onCloseLocation = () => {
    setViewLocation(false);
  }
  const onChangeLocation = async () => {
    if(locationId){
      const locationData = await API.Data.GetLocation(locationId);
      if(locationData){
          const id = locationData.location_id;
          const name = locationData.location_name;
          const color = locationData.location_color;
          Utility.Storage.Set(Code.LocalKey.LocationKey, Extract.Key.Encode(id!) || "");
          Utility.Storage.SetSecure(Code.LocalKey.LocationName, name || "");
          Utility.Storage.SetSecure(Code.LocalKey.LocationColor, color || "#0073aa");
      } 

      setViewLocation(false);
      window.location.reload();
    } 
 }

  interface TabLinkProps {
    id: number;
    title: string;
    list?: string;
    form?: string;
    name?: string;
  }
  
  const TabLink: React.FC<TabLinkProps> = ({ id, title, list, form, name }) => {
    const Privilege = Hooks.Privilege.List(id);
    return (
      <>
        {(Privilege.ALL || Privilege.VIEW) && ( 
          <div className={`mxtab mx${id}`}>
            {list ? <a onClick={() => openForm(title, list)()}>{ name ?  name : title}</a> : ''} 
            {form ? <a onClick={() => openForm(title, form)()}>+</a> : ''}
          </div>)
        }
      </>
    );
  };

  useEffect(() => {
    const onPageLoad = async () => {
      Utility.Set.PageTitle('Dashboard', 1);
      let userMenu;
      userMenu = await Files.Json.Fetch<Menu>(`menu${userId}`, setError, true);
      let menuCount = userMenu?.length || 0;
      if(menuCount==0){
        userMenu = await Files.Json.Fetch<Menu>('menu', setError, true);
        menuCount = userMenu?.length || 0;
      }
      if(menuCount==0){
        navigate("/");
      }
      setMenuData(userMenu || []);
      
  
      //Load user's profile photo
      const fileList = Utility.Storage.FileList();
       if(fileList){
        setProfileUrl(await Utility.Image.Blob('profile', fileList)); 
       }
    };
    onPageLoad();
  }, []);

  // Create a separate component for each menu item
  const TopMenu: React.FC<{ menu: any; index: number }> = ({ menu, index }) => {
    const Privilege = Hooks.Privilege.List(menu.value);
    
    if (!(Privilege.ALL || Privilege.VIEW)) {
      return null;
    }
    
    return (
      <a 
        key={index} 
        onClick={() => openForm(menu.label, menu.link)()} 
        data-path={menu.link} 
        data-title={menu.label} 
        accessKey={menu.key} 
        title={menu.info}
      >
        <img src={`assets/images/light/${menu.icon}.svg`} alt={menu.label} />
        <span>{menu.label}</span>
      </a>
    );
  };

  return (
    <div className='panel'>
      <div className='header' style={{backgroundColor: locationColor}}>

        {/* Menu Icon and Company Name */}
        <div className='menu'> {/* page-col32 page-tab12  page-mob10*/}
          <div className='icon' onClick={toggleMenuStrip} accessKey='l' title='Alt+L' id='left'><hr /></div>
          <div className='title' onClick={clearAllTabs}>{companyName} | {locationName}</div>
        </div>

        {/* Top Menus */}
        <div className='topmenu'>
          {menuData.map((menu, index) => (
            <TopMenu key={index} menu={menu} index={index} />
          ))}
        </div>

        {/* Profile and Setting Menus */}
        <div className='settings'> {/* page-col8 page-tab8  page-mob10 */}
          <div className='profile' ref={profileRef} onClick={toggleSetting}>
            <div className='user'>
              <span>{userName}</span>
              <div><img id='userimg' src={Utility.Image.User(profileUrl)}/></div>
              <p id='tasklist'></p>
            </div>
            {isSettingVisible && (
              <div className='menu'>
                <a className='icon-location' onClick={viewChangeLocation}>Change Location</a>
                {PriTasks.ALL || PriTasks.VIEW ? (
                  <a className='icon-task' onClick={() => openForm("Task", "tasks")()}>Tasks</a>
                ) : null}
                {PriNote.ALL || PriNote.VIEW ? (
                  <a className='icon-note' onClick={() => openForm("Notes", "note")()}>Notes</a>
                ) : null}
                {/* <a className='icon-time'>Time Clock</a>
                <a className='icon-setting'>Account Settings</a>
                <a className='icon-help'>Help</a> */}
                <a className='icon-logout' onClick={logoutClicked}>Logout</a>
              </div>
            )}
          </div>
        </div>

        {viewLocation && (
          <div className='location-change'>
            <p>Change Location?<a onClick={onCloseLocation}>X</a></p>
            <div>
              <Field.Select id={Name.Location.Id} value={locationId} options={selectedlocations}  onChange={(e) => setLocationId(Number(e.target.value))}  />
              <Button.Action title={'Submit'} info={"Click to change location"} onClick={onChangeLocation} />  
            </div>
         </div>
        )}

      </div>  

      {/* Tab Strip */}
      <div className={`tabstrip ${isMenuStripVisible ? 'menuactive' : ''}`}>
        <div id='tablist' className='tablink'>
          {visibleTabs.map((tab: Tab, index: number) => (
            <div
              key={index}
              id={`link${index + 1}`}
              vocab={`panel${index + 1}`}
              style={{ display: tab.visible ? 'block' : 'none' }}
              className={`tab ${activeTab === `panel${index + 1}` ? 'active' : ''}`}
              onClick={() => activateTab(`panel${index + 1}`)}
              onAuxClick={(e) => handleMiddleClick(e, index)}
            >
              {tab.title}
              <a onClick={(e) => { e.stopPropagation();  handleTabClose(index); }}>×</a>
            </div>
          ))}
        </div>
      </div>

      {/* Client Search */}
      <div className={`searchbox ${isMenuStripVisible ? 'menuactive' : ''}`}>
        <input
          type='text'
          value={searchKey}
          onChange={handleSearchChange}
          autoComplete="one-time-code"
          placeholder='Patient Name'
        />
          {results.length > 0 && (
            <div>
              <a onClick={() => clearSearch()}>×</a>
              <ul>
                {results.map(result => (
                  <li key={result.client_id} onClick={(event) => handleClientClick(event, `${result.client_first_name ||''} ${result.client_middle_name ||''} ${result.client_last_name ||''} #${result.client_number ||''}`, result.client_id!)}>
                    <span className='form-col10'>#{result.client_number}</span>
                    <span className='form-col25'>{result.client_first_name} {result.client_middle_name} {result.client_last_name}</span>
                    <span className='form-col35'>{result.client_address}</span>
                    <span className='form-col15'>{Utility.Format.DateOnly(result.client_dob)}</span>
                    <span className='form-col15'>{Utility.Format.Phone(result.client_phone)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
      </div> 

      {/* Reload button */}
      <div className={`reload ${isMenuStripVisible ? 'menuactive' : ''}`} onClick={reloadActiveTab}></div>
      
      {/* Tab Frames */}
      <div className={`tabframe ${isMenuStripVisible ? 'menuactive' : ''}`}>
        {visibleTabs.map((tab: Tab, index: number) => (
          <iframe
            key={index}
            id={`panel${index + 1}`}
            className={`frame ${activeTab === `panel${index + 1}` ? 'active' : ''}`}
            src={tab.link}
          ></iframe>
        ))}
      </div>
        
      {/* Left Menu Strip and its Menus */}
      {isMenuStripVisible && (
      <div className='menustrip'>
        <Control.Panel.LeftMenu title="Dashboard" activeMenu={activeMenu} thisMenu={10} toggleSubMenu={toggleSubMenu}><></></Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="Billing" activeMenu={activeMenu} thisMenu={11} toggleSubMenu={toggleSubMenu}
          subForms={[
            Code.Form.Id.CLIENT,
            Code.Form.Id.SALE,
            Code.Form.Id.ORDERS,
            Code.Form.Id.INVOICE,
            Code.Form.Id.DISPENSE,
            Code.Form.Id.ESTIMATE,
            Code.Form.Id.DELIVERY,
            Code.Form.Id.PROFORMA,
            Code.Form.Id.BILLING,
            Code.Form.Id.CLAIM,
            Code.Form.Id.REMITTANCE
          ]}>
          <TabLink id={Code.Form.Id.CLIENT} title="Patient" list="client" form="client/new" />
          <TabLink id={Code.Form.Id.SALE} title="Sale Form" list="sale" form="sale/new" />
          <TabLink id={Code.Form.Id.ORDERS} title="Order" list="orders" form="orders/new" />
          <TabLink id={Code.Form.Id.INVOICE} title="Invoice" list="invoice" form="invoice/new" />
          <TabLink id={Code.Form.Id.DISPENSE} title="Dispense" list="dispense" form="dispense/new" />
          <TabLink id={Code.Form.Id.ESTIMATE} title="Estimate" list="estimate" form="estimate/new" />
          <TabLink id={Code.Form.Id.DELIVERY} title="Delivery" list="delivery" form="delivery/new" />
          <TabLink id={Code.Form.Id.PROFORMA} title="Proforma" list="proforma" form="proforma/new" />
          <TabLink id={Code.Form.Id.BILLING} title="Billing" name="AHS Billing" list="billing" form="billing/new" />
          <TabLink id={Code.Form.Id.CLAIM} title="Claim" name="AHS Claim" list="claim" form="claim/new" />
          <TabLink id={Code.Form.Id.REMITTANCE} title="Remittance" name="AHS Remittance" list="remittance" form="remittance/edit" />
        </Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="CRM" activeMenu={activeMenu} thisMenu={12} toggleSubMenu={toggleSubMenu}
          subForms={[
            Code.Form.Id.CALENDAR, 
            Code.Form.Id.EXAM,
            Code.Form.Id.INTAKE,
            Code.Form.Id.BOOKING,
            Code.Form.Id.APPOINTMENT,
            Code.Form.Id.SCHEDULE,
            Code.Form.Id.DOCUMENT,
            Code.Form.Id.REFERRAL,
            Code.Form.Id.NOTE,
            Code.Form.Id.RECALL
          ]}>
          <TabLink id={Code.Form.Id.CALENDAR} title="Calendar" list="calendar/schedule" form="schedule/new" />
          <TabLink id={Code.Form.Id.EXAM} title="Exam" list="exam" form="exam/new" />
          <TabLink id={Code.Form.Id.INTAKE} title="Intake" list="intake" form="intake/new" />
          <TabLink id={Code.Form.Id.BOOKING} title="Online Booking" list="booking" />
          <TabLink id={Code.Form.Id.APPOINTMENT} title="Appointment" list="appointment" form="appointment/new" />
          <TabLink id={Code.Form.Id.SCHEDULE} title="Schedule" list="schedule" form="schedule/new" />
          <TabLink id={Code.Form.Id.DOCUMENT} title="Document" list="document" form="office/document/new" />
          <TabLink id={Code.Form.Id.REFERRAL} title="Referral" list="referral" form="referral/new" />
          <TabLink id={Code.Form.Id.NOTE} title="Notes" list="note" form="note/new" />
          <TabLink id={Code.Form.Id.RECALL} title="Recall" list="recall" form="recall/new" />
        </Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="Inventory" activeMenu={activeMenu} thisMenu={14} toggleSubMenu={toggleSubMenu} 
          subForms={[
            Code.Form.Id.INVENTORY, 
            Code.Form.Id.PURCHASE, 
            Code.Form.Id.PURCHASERETURN, 
            Code.Form.Id.PURCHASECREDIT, 
            Code.Form.Id.SALERETURN, 
            Code.Form.Id.STOCK, 
            Code.Form.Id.PRODUCT, 
            Code.Form.Id.SUPPLIER, 
            Code.Form.Id.CATEGORY, 
            Code.Form.Id.MISSING
          ]}>
          <TabLink id={Code.Form.Id.INVENTORY} title="Inventory" list="inventory" form="inventory" />
          <TabLink id={Code.Form.Id.PURCHASE} title="Purchase" list="purchase" form="purchase/spec?rc=0" />
          <TabLink id={Code.Form.Id.PURCHASERETURN} title="Purchase Return" list="purchasereturn" form="purchasereturn/new" />
          <TabLink id={Code.Form.Id.PURCHASECREDIT} title="Purchase Credit" list="purchasecredit" form="purchasecredit/new" />
          <TabLink id={Code.Form.Id.SALERETURN} title="Sale Return" list="salereturn" form="salereturn/new" />
          <TabLink id={Code.Form.Id.STOCK} title="Stock" list="stock" form="stock/new" />
          <TabLink id={Code.Form.Id.PRODUCT} title="Product" list="product" form="product/new" />
          <TabLink id={Code.Form.Id.SUPPLIER} title="Supplier" list="supplier" form="supplier/new" />
          <TabLink id={Code.Form.Id.CATEGORY} title="Category" list="category" form="office/category/new" />
          <TabLink id={Code.Form.Id.MISSING} title="Missing" list="missing" form="missing/new" />
        </Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="Accounts" activeMenu={activeMenu} thisMenu={13} toggleSubMenu={toggleSubMenu} 
          subForms={[
            Code.Form.Id.DAYBOOK, 
            Code.Form.Id.PAYMENT, 
            Code.Form.Id.PAYOUT, 
            Code.Form.Id.PAYEE, 
            Code.Form.Id.CREDIT,
            Code.Form.Id.OFFER, 
            Code.Form.Id.TAX
        ]}>
          <TabLink id={Code.Form.Id.DAYBOOK} title="Day Book" list="daybook" form="daybook/new" />
          <TabLink id={Code.Form.Id.PAYMENT} title="Payment" list="payment" form="payment/new" />
          <TabLink id={Code.Form.Id.PAYOUT} title="Payout" list="payout" form="payout/new" />
          <TabLink id={Code.Form.Id.PAYEE} title="Payee" list="payee" form="payee/new" />
          <TabLink id={Code.Form.Id.CREDIT} title="Credit" list="credit" form="credit/new" />
          <TabLink id={Code.Form.Id.OFFER} title="Offer" list="offer" form="account/offer/new" />
          <TabLink id={Code.Form.Id.TAX} title="Tax" list="tax" form="account/tax/new" />
        </Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="Reports" activeMenu={activeMenu} thisMenu={16} toggleSubMenu={toggleSubMenu}
          subForms={[
            Code.Form.Id.REPORTS, 
            Code.Form.Id.REPORT
          ]}>
          <TabLink id={Code.Form.Id.REPORTS} title="Reports" list="reports/custom" form="reports/custom"  />
          <TabLink id={Code.Form.Id.REPORT} title="Custom" list="report" form="report/new"  />
        </Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="Admin" activeMenu={activeMenu} thisMenu={17} toggleSubMenu={toggleSubMenu}
          subForms={[
            Code.Form.Id.DOCTOR,
            Code.Form.Id.RECDOCTOR,
            Code.Form.Id.INSURANCE,
            Code.Form.Id.INSURER,
            Code.Form.Id.BANK,
            Code.Form.Id.CITY,
            Code.Form.Id.USER,
            Code.Form.Id.ROLE,
          ]}>
          <TabLink id={Code.Form.Id.DOCTOR} title="Doctor" list="doctor" form="doctor/new" />
          <TabLink id={Code.Form.Id.RECDOCTOR} title="Receiving Doctor" list="recdoctor" form="recdoctor/new" />
          <TabLink id={Code.Form.Id.INSURANCE} title="Insurance" list="insurance" form="insurance/new" />
          <TabLink id={Code.Form.Id.INSURER} title="Insurer" list="insurer" form="insurer/new" />
          <TabLink id={Code.Form.Id.BANK} title="Bank" list="bank" form="bank/new" /> 
          <TabLink id={Code.Form.Id.CITY} title="City" list="city" form="city/new" />
          <TabLink id={Code.Form.Id.USER} title="User" list="user" form="user/new" />
          <TabLink id={Code.Form.Id.ROLE} title="Role" list="role" form="role/new" />
        </Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="Setting" activeMenu={activeMenu} thisMenu={19} toggleSubMenu={toggleSubMenu}
          subForms={[
            Code.Form.Id.OPTION, 
            Code.Form.Id.TEMPLATE,
            Code.Form.Id.EXAMTEMPLATE,
            Code.Form.Id.EXAMTYPE,
            Code.Form.Id.SUBMITTER,
            Code.Form.Id.TERM,
            Code.Form.Id.LOCATION
          ]}>
          <TabLink id={Code.Form.Id.OPTION} title="Option" list="option" form="option/new" />
          <TabLink id={Code.Form.Id.TEMPLATE} title="Referral Template" list="template" form="template/new" />
          <TabLink id={Code.Form.Id.EXAMTEMPLATE} title="Exam Template" list="examtemplate" form="examtemplate/new" />
          <TabLink id={Code.Form.Id.EXAMTYPE} title="Exam Type" list="examtype" form="examtype/new" />
          <TabLink id={Code.Form.Id.SUBMITTER} title="Submitter" list="submitter" form="submitter/new" />
          <TabLink id={Code.Form.Id.TERM} title="Terms" list="term" form="term/new" />
          <TabLink id={Code.Form.Id.LOCATION} title="Location" list="location" form="location/new" />
          <TabLink id={Code.Form.Id.COMPANY} title="Company" list="company" form="company/new" />
        </Control.Panel.LeftMenu>
        <Control.Panel.LeftMenu title="More" activeMenu={activeMenu} thisMenu={18} toggleSubMenu={toggleSubMenu}
           subForms={[
            Code.Form.Id.COMMUNICATION,
            Code.Form.Id.PROJECT,
            Code.Form.Id.TASKS,
            Code.Form.Id.INQUIRY,
          ]}>
          <TabLink id={Code.Form.Id.COMMUNICATION} title="Communication" list="communication" form="communication/new" />
          <TabLink id={Code.Form.Id.PROJECT} title="Project" list="project" form="project/new" />
          <TabLink id={Code.Form.Id.TASKS} title="Tasks" list="tasks" form="office/tasks/new" />
          <TabLink id={Code.Form.Id.INQUIRY} title="Inquiry" list="inquiry" form="inquiry/new" />
          <div><a onClick={logoutClicked}>Logout</a><a></a></div>
        </Control.Panel.LeftMenu>
      </div>
      )}
      <Actions.List />
    </div>
    // <TabLink id={Code.Form.Id.STOCK} title="Stock" list="item" form="item" />
    // <TabLink id={Code.Form.Id.PRESCRIPTION} title="Prescription" list="prescription" form="prescription/new" />
    // <TabLink id={Code.Form.Id.PURCHASERETURN} title="Purchase Return" list="preturn" form="preturn/new" />
    // <TabLink id={Code.Form.Id.PURCHASEORDER} title="Purchase Order" list="porder" form="porder/new" />
    // <TabLink id={Code.Form.Id.SALERETURN} title="Sale Return" list="sreturn" form="sreturn/new" /> 
    // <TabLink id={Code.Form.Id.REQUISITION} title="Requisition" list="requisition" form="requisition/new" />
    // <TabLink id={Code.Form.Id.ISSUE} title="Issue" list="issue" form="issue/new" /> 
    // <TabLink id={Code.Form.Id.OFFER} title="Offer" list="offier" form="offer/new" /> 
    // <TabLink id={Code.Form.Id.CREDIT} title="Credit" list="credit" form="credit/new" />
    // <TabLink id={Code.Form.Id.CAMPAIGN} title="Campaign" list="campaign" form="campaign/new" />
    // <TabLink id={Code.Form.Id.MESSENGER} title="Messenger" list="messenger" form="messenger/new" />
    // <TabLink id={Code.Form.Id.COMMUNICATION} title="Communication" list="communication" form="communication/new" />
    // <TabLink id={Code.Form.Id.SETTINGS} title="Account Settings" list="settings" />
    // <TabLink id={Code.Form.Id.SETTINGS} title="Lazywest" list="import/lazywest/client" />
    // <TabLink id={Code.Form.Id.MERGE} title="Merge" list="merge" />
    // <TabLink id={Code.Form.Id.TAX} title="Tax" list="tax" form="tax/new" /> 
    // <TabLink id={Code.Form.Id.LOCATION} title="Location" list="location" form="location/new" />
    // <TabLink id={Code.Form.Id.TIME} title="Time Sheet" list="attendance" form="attendance/new" />
    // <TabLink id={Code.Form.Id.SHIFT} title="Shift" list="shift" form="shift/new" />
    // <TabLink id={Code.Form.Id.DEPARTMENT} title="Department" list="department" form="department/new" />
    // <TabLink id={Code.Form.Id.DESIGNATION} title="Designation" list="designation" form="designation/new" />
    // <TabLink id={Code.Form.Id.HOLIDAY} title="Holiday" list="holiday" form="holiday/new" />
    // <TabLink id={Code.Form.Id.LEAVE} title="Leave" list="leave" form="leave/new" />
    // <TabLink id={Code.Form.Id.WORKHOUR} title="Work Hour" list="workhour" form="workhour/new" />
    // <TabLink id={Code.Form.Id.EMPLOYEE} title="Employee" list="employee" form="employee/new" />
    // <TabLink id={Code.Form.Id.RECRUITMENT} title="Recruitment" list="recruitment" form="recruitment/new" />
  );
};

export default Panel;